import React, { useCallback, memo, useEffect, useState } from 'react';
import { Select, ChipSelect } from '@leasequery/lq.react-component-library';
import { SelectOption } from '@leasequery/lq.react-component-library/dist/models';
import { ReportCurrency, ClientCustomCalendarModel } from '../../../models';
import * as Styled from './styled';
import { buildDropdownList } from './helpers/buildReportSettingData';
import { getRunByPlaceholderText } from '../helpers/getRunByPlaceholderText';
import { ReportTypeSetting } from '../../../models/reportTypeSetting';
import { Icon } from '../../../components';
import { buildAllocationOptions } from './helpers/buildClientAllocationOptions';
import { useAuth } from '../../../context';

interface ReportSettingDisclosureProps {
  onChange: (updatedValue: ReportSettingDisclosureState) => void;
  reportSettingState: ReportSettingDisclosureState;
  reportTypeSetting: ReportTypeSetting;
  isAllowPIT: boolean;
  customCalendarSettings?: ClientCustomCalendarModel;
}

export interface ReportSettingDisclosureState {
  selectedCurrency: SelectOption<ReportCurrency>;
  startDate: string;
  endDate: string;
  selectedViewBy: SelectOption<string>;
  selectedAllocations?: SelectOption<string>[];
  isValid: boolean;
}

function ReportSettingDisclosure({
  onChange,
  reportSettingState,
  reportTypeSetting,
}: ReportSettingDisclosureProps): JSX.Element {
  const [isAllocationSelected, setIsAllocationSelected] = useState(false);
  const [isDateRangeValid, setIsDateRangeValid] = useState(
    reportSettingState.isValid,
  );

  useEffect(() => {
    setIsDateRangeValid(reportSettingState.isValid);
    // TODO: COR-19256 refactor to allow isValid to be updated via global onChange
    // eslint-disable-next-line
  }, [reportSettingState.startDate, reportSettingState.endDate]);

  const runByList = buildDropdownList(
    'Disclosure',
    'ViewByOptions',
    reportTypeSetting,
  );
  const isDisableRunBy = runByList.length === 1;
  const { clientSetting } = useAuth();

  const availableAllocations = useCallback(() => {
    const selectedViewByValue = reportSettingState.selectedViewBy?.value;

    const allocationOptions = buildAllocationOptions(
      reportTypeSetting.reportTypes,
    );

    if (!selectedViewByValue) {
      return allocationOptions[0]?.children ?? [];
    }

    const selectedByAllocationOptions = allocationOptions.find(
      (x) => x.parentId.toString() === selectedViewByValue,
    );

    return selectedByAllocationOptions
      ? selectedByAllocationOptions.children
      : [];
  }, [reportTypeSetting, reportSettingState]);

  useEffect(() => {
    if (reportSettingState.selectedViewBy?.value) {
      setIsAllocationSelected(true);
      return;
    }
    setIsAllocationSelected(false);
  }, [reportSettingState.selectedViewBy, runByList]);

  const runBySelectionHandler = useCallback(
    (option: SelectOption<string>) => {
      if (option.value) {
        onChange({
          ...reportSettingState,
          selectedViewBy: option,
          isValid: false,
          selectedAllocations: [],
        });
      } else {
        onChange({
          ...reportSettingState,
          selectedViewBy: option,
          selectedAllocations: [],
          isValid: isDateRangeValid,
        });
      }
    },
    [onChange, reportSettingState, isDateRangeValid],
  );

  const allocationSelectionHandler = useCallback(
    (options: SelectOption<string>[]) => {
      if (isAllocationSelected && options.length < 1) {
        onChange({ ...reportSettingState, isValid: false });
      } else {
        onChange({
          ...reportSettingState,
          isValid: isDateRangeValid,
          selectedAllocations: options,
        });
      }
    },
    [onChange, reportSettingState, isAllocationSelected, isDateRangeValid],
  );

  return (
    <>
      {/* if allocations are enabled display selector */}
      {!isDisableRunBy && (
        <>
          <Styled.Line />
          <Styled.DropdownBar>
            <div>
              <Icon name="gear" />
            </div>
            <div>
              <Select
                label="Run By"
                name="RunBy"
                options={runByList}
                onChange={(option: SelectOption<string>): void =>
                  runBySelectionHandler(option)
                }
                value={reportSettingState.selectedViewBy}
                disabled={isDisableRunBy}
              />
            </div>
            <div>
              <ChipSelect
                name={'AllocationSelector'}
                options={availableAllocations()}
                onChange={(option: SelectOption<string>[]): void =>
                  allocationSelectionHandler(option)
                }
                value={reportSettingState.selectedAllocations}
                disabled={!isAllocationSelected}
                placeholder={getRunByPlaceholderText(clientSetting.isCoaEnabled)}
              />
            </div>
          </Styled.DropdownBar>
        </>
      )}
      <Styled.Line />
    </>
  );
}

export default memo(ReportSettingDisclosure);
